import React, { useState, useEffect, useRef } from "react";
import CustomAxios from "../../utils/CustomAxios";
import axios from "axios";
import { Button, Typography, Box } from "@mui/material";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';


const ProfileSettings = () => {
    const [avatars, setAvatars] = useState([]);
    const [loading, setLoading] = useState(false);
    const fileInputRef = useRef(null);
    const toast = useRef(null);

    // Fetch avatars from backend
    const fetchAvatars = async () => {
        try {
            const res = await CustomAxios.get("/avatars/");
            setAvatars(res.data);
        } catch (err) {
            console.error("Error fetching avatars:", err);
        }
    };

    useEffect(() => {
        fetchAvatars();
    }, []);

    // Trigger hidden file input
    const handleAddProfileClick = () => {
        fileInputRef.current.click();
    };

    // Handle file selection & upload
    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file size: max 5MB
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'File size should not exceed 5MB', life: 3000 });
            e.target.value = "";
            return;
        }

        // Validate file type: only jpeg/png
        const isValidType =
            file.type === 'image/jpeg' ||
            file.type === 'image/png' ||
            /\.(jpg|jpeg|png)$/i.test(file.name);
        if (!isValidType) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'File type should be jpeg or png', life: 3000 });
            e.target.value = "";
            return;
        }

        setLoading(true);
        try {
            // Step 1: Request SAS URL + stable URL
            const res = await CustomAxios.post("/avatars/upload/", {
                file_name: file.name,
            });
            const uploadUrl = res?.data?.upload_url;
            const stableUrl = res?.data?.stable_url;

            console.log("uploadUrl:", uploadUrl);
            console.log("stableUrl:", stableUrl);


            if (!uploadUrl?.startsWith("http")) {
                throw new Error("Invalid uploadUrl: " + uploadUrl);
            }


            // Step 2: Upload file to Azure
            await axios.put(uploadUrl, file, {
                headers: { "x-ms-blob-type": "BlockBlob" },
            });
            console.log('kee')


            // Step 3: Notify backend that upload is done
            await CustomAxios.post("/avatars/confirm/", {
                file_name: file.name,
                blob_url: stableUrl,
            });

            toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile uploaded successfully!', life: 3000 });
            fetchAvatars();
        } catch (err) {
            console.error("Upload error:", err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: `Upload failed: ${err?.message || ''}`, life: 3000 });
        } finally {
            setLoading(false);
            e.target.value = ""; // reset input
        }
    };


    // Ask for confirmation for activate/deactivate
    const toggleActive = (avatarId, currentStatus) => {
        const confirmMode = currentStatus ? "deactivate" : "activate";
        const actionText = currentStatus ? "deactivate" : "activate";

        confirmDialog({
            message: (
                <Box sx={{ display: "flex", gap: "10px", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: "16px", fontWeight: 500 }}>
                        Do you want to {actionText} this profile image?
                    </Typography>
                </Box>
            ),
            header: "Confirm",
            defaultFocus: 'reject',
            acceptClassName: confirmMode === "deactivate" ? 'p-button-danger' : 'p-button-success',
            accept: () => confirmToggle(avatarId, confirmMode),
        });
    };

    const confirmToggle = async (avatarId, confirmMode) => {
        try {
            if (confirmMode === "deactivate") {
                await CustomAxios.delete(`/avatars/${avatarId}/delete/`);
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile deactivated successfully!', life: 3000 });
            } else {
                await CustomAxios.patch(`/avatars/${avatarId}/`, { is_active: true });
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Profile activated successfully!', life: 3000 });
            }
            await fetchAvatars();
        } catch (err) {
            console.error("Error confirming toggle:", err);
            toast.current.show({ severity: 'error', summary: 'Error', detail: `Failed to ${confirmMode} profile`, life: 3000 });
        }
    };

    return (
        <div style={{ padding: "30px" }}>
            <Toast ref={toast} />
            <ConfirmDialog />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px", marginTop: "15px" }}>
                <Typography sx={{ fontSize: "16px", fontWeight: "700", lineHeight: "24px" }}>Profile Settings</Typography>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", lineHeight: "22px" }}>Profile Details</Typography>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "20px" }}>
                <Box />
                <Box sx={{ display: "flex", gap: "20px" }}>
                    <Button variant="contained" sx={{ textTransform: "none" }} onClick={handleAddProfileClick} disabled={loading}>
                        <Typography sx={{ fontSize: { sm: "12px", md: "14px" }, fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                            {loading ? "Uploading..." : "Add Profile"}
                        </Typography>
                    </Button>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png"
                    />
                </Box>
            </Box>

            {/* Stats Bar: Total, Active, Inactive */}
            <Box sx={{ height: "94px", width: "100%", backgroundColor: "white", display: "flex", alignItems: "center", mb: 3, borderRadius: "4px" }}>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Total Profiles
                    </Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.filter(a => a.is_active).length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Active Profiles
                    </Typography>
                </Box>
                <Box sx={{ width: "100%" }}>
                    <Typography sx={{ fontSize: "24px", fontWeight: "500", lineHeight: "32px", textAlign: "center" }}>
                        {avatars.length - avatars.filter(a => a.is_active).length}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px", textAlign: "center" }}>
                        Inactive Profiles
                    </Typography>
                </Box>
            </Box>

            {/* Avatars Grid */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                    gap: "20px",
                }}
            >
                {avatars.map((avatar) => (
                    <div
                        key={avatar.id}
                        style={{
                            border: "1px solid #ddd",
                            borderRadius: "10px",
                            padding: "10px",
                            textAlign: "center",
                            backgroundColor: "#FAFAFA",
                        }}
                    >
                        <img
                            src={avatar.view_url || avatar.blob_url}
                            alt="avatar"
                            style={{
                                width: "100%",
                                height: "120px",
                                objectFit: "cover",
                                borderRadius: "8px",
                            }}
                        />
                        <p
                            title={avatar.file_name}
                            style={{
                                margin: "10px 0",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {avatar.file_name}
                        </p>
                        <button
                            onClick={() => toggleActive(avatar.id, avatar.is_active)}
                            style={{
                                padding: "5px 10px",
                                borderRadius: "5px",
                                backgroundColor: avatar.is_active ? "#28a745" : "#dc3545",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            {avatar.is_active ? "Active" : "Inactive"}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProfileSettings;


